import React from "react";
import { useParams } from "react-router-dom";
import "./serviceDetail.scss";

const serviceData = {
  "Brand Audit & Consulting": {
  title: "Brand Audit & Consulting",
  details: "Comprehensive analysis to optimize your brand position...",
  image: "/branding.avif",
  process: "We analyze brand positioning, assess competitors, and create a strategy to improve market presence.",
  howWeDoIt: `
    Our brand audit and consulting process involves a thorough analysis of your brand’s identity, presence, and market perception.
    
    1. Brand Identity Review – We assess your brand’s logo, typography, color scheme, and overall messaging to ensure consistency and alignment with your business goals.
    
    2. Market & Competitor Analysis – By evaluating your competitors and industry trends, we determine what sets you apart and how to leverage your strengths effectively.
    
    3. Customer Feedback & Perception – We conduct surveys, analyze online reviews, and assess customer sentiment to understand how people perceive your brand.
    
    4. Digital Presence Evaluation – Your website, social media, and digital marketing strategies are examined to identify opportunities for improvement.
  `,
  howWeAreDifferent: `
    - Data-driven Approach: Our brand audits are backed by research and real customer insights.
    - Customized Strategies: We provide tailored solutions instead of generic branding advice.
    - Holistic Perspective: We consider all brand touchpoints—both digital and offline.
  `,
  whatWeCanDo: `
    - Refine your brand messaging to connect better with your audience.
    - Improve consistency across all platforms to strengthen brand identity.
    - Develop a strong positioning strategy that differentiates you from competitors.
  `,
  results: `
    ✅ Increased brand trust and loyalty  
    ✅ Higher customer engagement  
    ✅ Improved competitive positioning in the market  
  `
},

"Lead Generation": {
  title: "Lead Generation",
  details: "Proven strategies to generate and nurture leads...",
  image: "/lead.avif",
  process: "We use targeted ads, landing pages, and email funnels to attract and convert high-quality leads.",
  howWeDoIt: `
    Our lead generation approach is designed to attract high-quality prospects and convert them into customers.
    
    1. Audience Targeting & Research – We analyze customer demographics and behavior to create detailed buyer personas.
    
    2. Multi-Channel Marketing – We use Facebook Ads, Google Ads, LinkedIn outreach, and email campaigns to drive leads.
    
    3. Optimized Landing Pages – High-converting landing pages with strong CTAs increase conversion rates.
    
    4. Automated Follow-Ups – We implement email drip campaigns and retargeting ads to nurture leads.
  `,
  howWeAreDifferent: `
    - Focus on Quality Over Quantity: We generate high-converting leads, not just random contacts.
    - AI-Powered Targeting: Advanced algorithms help optimize ads for the right audience.
    - Continuous Optimization: We analyze data and adjust campaigns in real-time.
  `,
  whatWeCanDo: `
    - Attract your ideal customers through strategic ad placement.
    - Optimize your lead funnel to reduce drop-off rates.
    - Increase your conversion rate with tested marketing strategies.
  `,
  results: `
    ✅ More high-quality leads  
    ✅ Lower cost per acquisition  
    ✅ Higher sales conversion rates  
  `
},


  "Creative Content": {
  title: "Creative Content",
  details: "Content that connects with audiences and builds trust...",
  image: "/content.avif",
  process: "We craft engaging blog posts, videos, and social media content tailored to your audience.",
  howWeDoIt: `
    1. Content Strategy Development – We create a customized roadmap based on industry trends and audience needs.
    
    2. Multi-Format Content Creation – We produce blogs, videos, infographics, and social media content.
    
    3. SEO & Engagement Optimization – We optimize for search engines and social media algorithms.
    
    4. Content Distribution & Promotion – We maximize reach through strategic distribution.
  `,
  howWeAreDifferent: `
    - Storytelling Approach: We craft compelling narratives that engage audiences.  
    - SEO-Optimized Content: Our content is designed to rank high on search engines.  
    - Data-Backed Strategies: Every content piece is created with a goal-driven approach.
  `,
  whatWeCanDo: `
    - Enhance your brand authority with high-quality, informative content.  
    - Improve engagement rates on social media and blogs.  
    - Drive organic traffic to your website.  
  `,
  results: `
    ✅ Higher audience engagement  
    ✅ Increased brand credibility  
    ✅ Better SEO rankings  
  `
},

"Web Development": {
  title: "Web Development",
  details: "Build responsive, fast, and user-friendly websites...",
  image: "/website.avif",
  process: "We develop modern websites using the latest technologies like React, WordPress, and Shopify.",
  howWeDoIt: `
    1. UI/UX Design: We create visually appealing and user-friendly website designs.
    
    2. Custom Development: We build custom websites using technologies like ReactJS, WordPress, and Shopify.
    
    3. SEO Optimization: We ensure websites are optimized for search engines and mobile-friendly.
    
    4. Performance Optimization: We focus on speed, security, and seamless navigation.
  `,
  howWeAreDifferent: `
    - Custom Solutions: We don’t use templates; we build unique websites for your brand.  
    - SEO & Performance-Focused: Our websites are designed to rank well and load quickly.  
    - Scalable & Secure: We build websites that grow with your business.
  `,
  whatWeCanDo: `
    - Create a stunning website that represents your brand.  
    - Improve website loading speed for better user experience.  
    - Integrate e-commerce functionality to sell products efficiently.  
  `,
  results: `
    ✅ Higher website traffic  
    ✅ Improved conversion rates  
    ✅ Better brand credibility online  
  `
},

  "Digital Marketing": {
  title: "Digital Marketing",
  details: "Maximize online visibility and business growth...",
  image: "/dm.avif",
  process: "We create data-driven digital marketing strategies, including social media marketing, email marketing, and conversion rate optimization.",
  howWeDoIt: `
    1. Market Research & Strategy Development – We analyze your target audience, competitors, and industry trends to create a winning strategy.
    
    2. Content Marketing & SEO – We create valuable content that ranks on Google and drives organic traffic.
    
    3. Paid Advertising & PPC – We launch highly targeted Facebook, Google, and Instagram ad campaigns for lead generation.
    
    4. Email Marketing & Automation – We implement automated email funnels to nurture leads and increase conversions.
    
    5. Analytics & Performance Optimization – We track KPIs and refine strategies to maximize ROI.
  `,
  howWeAreDifferent: `
    - Data-Driven Approach: We use real-time analytics to optimize marketing efforts.  
    - Multi-Channel Strategies: We integrate multiple platforms to enhance reach.  
    - Continuous Optimization: We tweak campaigns to improve performance consistently.
  `,
  whatWeCanDo: `
    - Increase brand awareness with strategic digital campaigns.  
    - Improve customer engagement through social media and email marketing.  
    - Boost conversions and sales using high-performance ad campaigns.  
  `,
  results: `
    ✅ Higher website traffic  
    ✅ Better conversion rates  
    ✅ Improved customer engagement  
  `
},

"E-commerce Solutions": {
  title: "E-commerce Solutions",
  details: "Complete solutions to launch and grow your online store...",
  image: "/ecommerce.avif",
  process: "We provide Shopify, WooCommerce, and Magento-based e-commerce solutions, including store setup, product optimization, and customer acquisition strategies.",
  howWeDoIt: `
    1. Custom E-commerce Store Development – We build tailored Shopify, WooCommerce, and Magento stores.
    
    2. Product Optimization & SEO – We optimize product listings and descriptions for higher visibility.
    
    3. Marketing & Customer Retention – We implement abandoned cart recovery, upselling, and email campaigns.
    
    4. Secure Payment & Logistics Integration – We integrate multiple payment gateways and shipping solutions.
  `,
  howWeAreDifferent: `
    - Complete E-commerce Strategy: We go beyond development to focus on growth and conversion.  
    - Data-Backed Decisions: We analyze trends to optimize product positioning.  
    - Custom UI/UX Optimization: We create designs that enhance customer experience.
  `,
  whatWeCanDo: `
    - Launch and scale your online store with customized solutions.  
    - Improve customer experience for higher retention rates.  
    - Optimize product pages to increase conversions and revenue.  
  `,
  results: `
    ✅ Higher online sales  
    ✅ Better customer retention  
    ✅ Increased conversion rates  
  `
},

"Search Engine Optimization (SEO)": {
  title: "Search Engine Optimization (SEO)",
  details: "Improve rankings and increase organic traffic...",
  image: "/seo.avif",
  process: "We perform keyword research, on-page and off-page optimization, technical SEO, and link-building strategies to boost your site's search visibility.",
  howWeDoIt: `
    1. Keyword Research & Optimization – We identify high-ranking keywords to improve search visibility.
    
    2. On-Page SEO & Content Strategy – We optimize web pages, meta tags, and blog content for better rankings.
    
    3. Technical SEO & Site Speed Optimization – We fix site errors, improve mobile-friendliness, and boost loading speed.
    
    4. Link Building & Authority Growth – We acquire high-quality backlinks to increase domain authority.
    
    5. Local SEO Optimization – We optimize Google My Business listings to enhance local search rankings.
  `,
  howWeAreDifferent: `
    - Sustainable SEO Strategies: We focus on long-term rankings rather than shortcuts.  
    - Comprehensive Optimization: We cover all aspects of SEO, from content to technical improvements.  
    - Continuous Monitoring: We track performance and adjust strategies accordingly.
  `,
  whatWeCanDo: `
    - Boost your website ranking for relevant search terms.  
    - Drive high-quality organic traffic to your site.  
    - Improve website credibility and trust with strong SEO strategies.  
  `,
  results: `
    ✅ Higher search rankings  
    ✅ More organic traffic  
    ✅ Increased leads and conversions  
  `
},

"Pay-Per-Click (PPC) Advertising": {
  title: "Pay-Per-Click (PPC) Advertising",
  details: "Drive targeted traffic with effective ad campaigns...",
  image: "/ppc.avif",
  process: "We manage Google Ads and Facebook Ads campaigns, optimizing budgets and targeting the right audience to maximize conversions.",
  howWeDoIt: `
    1. Target Audience Analysis & Research – We identify and segment the ideal customer base.
    
    2. Ad Copy & Landing Page Optimization – We create high-converting ad copies and A/B test landing pages.
    
    3. Bid Management & Budget Optimization – We strategically allocate budgets for the best ROI.
    
    4. Performance Tracking & Scaling – We analyze campaign data and scale successful ads.
  `,
  howWeAreDifferent: `
    - Conversion-Focused Campaigns: We prioritize ROI over clicks.  
    - Advanced Audience Targeting: We use AI and machine learning for precise targeting.  
    - Data-Driven Optimization: We refine campaigns based on performance insights.
  `,
  whatWeCanDo: `
    - Maximize your ad budget by targeting high-converting audiences.  
    - Improve ad quality scores to reduce cost-per-click.  
    - Increase revenue through well-optimized ad campaigns.  
  `,
  results: `
    ✅ Higher ad conversion rates  
    ✅ Lower cost per acquisition  
    ✅ Increased return on investment (ROI)  
  `
},

"Social Media Management": {
  title: "Social Media Management",
  details: "Engage and grow your audience on social platforms...",
  image: "/smm.avif",
  process: "We create engaging content, manage social media accounts, run ad campaigns, and analyze performance to grow your online presence.",
  howWeDoIt: `
    1. Content Creation & Scheduling – We produce high-quality visuals, videos, and written content.
    
    2. Engagement & Community Building – We actively respond to comments and messages to build brand relationships.
    
    3. Ad Campaigns & Paid Promotions – We run Facebook, Instagram, and LinkedIn ads to expand reach.
    
    4. Analytics & Performance Optimization – We track engagement metrics and optimize social strategies.
  `,
  howWeAreDifferent: `
    - Authentic Engagement Strategies: We focus on community building, not just numbers.  
    - Data-Driven Social Media Growth: We analyze trends and audience insights.  
    - Cross-Platform Consistency: We ensure a unified brand voice across all platforms.
  `,
  whatWeCanDo: `
    - Enhance brand reputation through active social engagement.  
    - Increase followers and engagement rates with strategic content.  
    - Run successful ad campaigns for lead generation and conversions.  
  `,
  results: `
    ✅ Higher audience engagement  
    ✅ Stronger brand presence  
    ✅ More leads and conversions from social media  
  `
}

};


const ServiceDetail = () => {
  const { serviceTitle } = useParams();
  const service = serviceData[decodeURIComponent(serviceTitle)];

  if (!service) {
    return <h2>Service Not Found</h2>;
  }

  return (
    <div className="service-detail">
      <div className="content-container">
        {/* Left Section - Image & Details */}
        <div className="service-image">
          <img src={service.image} alt={service.title} />
          <p className="service-process"> How We Provide {service.title}:</p>
          <p>{service.process}</p>

          <h3> How We Do It</h3>
          <p>{service.howWeDoIt}</p>

          <h3> How We Are Different</h3>
          <p>{service.howWeAreDifferent}</p>

          <h3> What We Can Do for Your Brand</h3>
          <p>{service.whatWeCanDo}</p>

          <h3> Expected Results</h3>
          <p>{service.results}</p>
        </div>

        {/* Right Section - Inquiry Form */}
        <div className="inquiry-form">
          <h2>Enquire About {service.title}</h2>
          <form>
            <input type="text" placeholder="Your Name" required />
            <input type="number" placeholder="Your Number" required />
            <input type="email" placeholder="Your Email" required />
            <textarea placeholder="Your Message" required></textarea>
            <button type="submit">Submit Inquiry</button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetail;
